import type { DocumentsRootFolderDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { Tiles } from "@/design-system/Tiles";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { RootFolder } from "@/modules/documents/components/RootFolder";
import { canManageAllDocuments } from "@/modules/documents/permissions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface DocumentRootListProps {
  rootList: DocumentsRootFolderDto[];
  onDeleteFolder: (param: RootFolderDeleteParam) => Promise<unknown>;
}

export function DocumentRootList(props: DocumentRootListProps): React.ReactNode {
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const { componentProps, openDeleteModal } = useDeleteModal<RootFolderDeleteParam>("document-root-delete-modal");
  const hasPermission = usePermission();
  const sessionUser = useSessionUser();

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.document.root.list.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.document.root.list.title"),
              },
            ]}
          />
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{ scope: "project", entity: "documents", page: "list" }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.documents.suggestion.1"),
                t("component.ask-nobi-chat-drawer.documents.suggestion.2"),
              ]}
            />
            {hasPermission(canManageAllDocuments) && (
              <Button type="link" href={routes.documents.rootCreate({ slug })} data-testid="document-root-create-page">
                {t("page.document.root.list.action.new")}
              </Button>
            )}
          </>
        }
      >
        <Tiles as="ul" data-testid="documents-root-list" columns={{ base: 1, container: { md: 2 } }} gap="md">
          {props.rootList.map((folder) => (
            <li className="list-none" key={folder.id}>
              <RootFolder folder={folder} onDelete={openDeleteModal} />
            </li>
          ))}
        </Tiles>
      </DocumentPage>
      <DeleteModal<RootFolderDeleteParam>
        title={t("model.document.root.action.delete.confirmation")}
        description={t("model.document.root.action.delete.description")}
        onDelete={props.onDeleteFolder}
        {...componentProps}
      />
    </>
  );
}

type RootFolderDeleteParam = string;
